import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { canSeeBinauralBeats } from '@/lib/constants';
import { useProfile } from '@/lib/providers';
import { AdminRole } from '@/types/custom';

import {
  Content,
  ContentWithConfig,
  ContentType,
  ContentConfig,
  Keyword,
} from '@/types/custom';

/**
 * Fetch a list of available content items of a given type (e.g. "meditation").
 * Optionally include configurations for each content item.
 * Returns in older from newest (created) to oldest.
 */
export function useContentList(
  contentType: ContentType | '*',
  deps: any[] = [],
) {
  const [contentWithConfigs, setContentWithConfigs] = useState<
    ContentWithConfig[]
  >([]);
  const [loading, setLoading] = useState(true);
  const { profile } = useProfile();
  // Binaural beats are not released yet, so they are filtered out here rather
  // than at every call site -- this hook feeds the home grid, the catalogues,
  // search and favourites alike. Admins see the full library.
  const showBinaural = canSeeBinauralBeats(profile?.role === AdminRole);

  useEffect(() => {
    fetchContent();
  }, [contentType, ...deps]);

  /**
   * Fetches content of the specified type, joining with configs and keywords.
   */
  async function fetchContent() {
    try {
      setLoading(true);

      let query = supabase.from('content').select(
        `
          *,
          content_configs(*),
          keywords(*)
        `,
      );

      if (contentType !== '*') {
        query = query.eq('content_type', contentType);
      }

      const { data, error } = await query.order('created_at', {
        ascending: false,
      });

      if (error) throw error;

      const transformedData = (data || []).map(
        (item) =>
          ({
            content: {
              id: item.id,
              title: item.title,
              description: item.description,
              body: item.body,
              image_url: item.image_url,
              content_type: item.content_type,
              published: item.published,
              scope_type: item.scope_type,
              scope_id: item.scope_id,
              sort_order: item.sort_order,
              role_scope: item.role_scope,
              hnh_scope_type: item.hnh_scope_type,
              hnh_scope_id: item.hnh_scope_id,
              created_at: item.created_at,
              updated_at: item.updated_at,
            } satisfies Content,
            configs: (item.content_configs || []) satisfies ContentConfig[],
            keywords: item.keywords satisfies Keyword[],
          }) satisfies ContentWithConfig,
      );

      setContentWithConfigs(transformedData);
    } catch (error) {
      console.error('Error fetching content:', error);
    } finally {
      setLoading(false);
    }
  }

  return {
    contentWithConfigs: showBinaural
      ? contentWithConfigs
      : contentWithConfigs.filter(
          (item) => item.content.content_type !== 'binaural',
        ),
    loading,
  };
}

/**
 * Fetch a specific content item (e.g. meditation) and its associated configurations.
 * Supports passing contentId === "" as noop.
 * TODO: support including keywords here too.
 */
export function useContentWithConfig(contentId: string) {
  const [contentWithConfig, setContentWithConfig] =
    useState<ContentWithConfig | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!contentId) return; // noop

    fetchContentWithConfig();

    // Subscribe to changes in both content and content_configs tables
    const contentChannel = supabase
      .channel('content-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'content' },
        fetchContentWithConfig,
      )
      .subscribe();

    const configChannel = supabase
      .channel('content-config-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'content_configs' },
        fetchContentWithConfig,
      )
      .subscribe();

    // Cleanup subscriptions on unmount
    return () => {
      contentChannel.unsubscribe();
      configChannel.unsubscribe();
    };
  }, [contentId]); // Refetch when contentId changes

  /**
   * Fetches a specific content item and its associated configurations
   */
  async function fetchContentWithConfig() {
    try {
      setLoading(true);

      // Fetch the content item
      const { data: contentData, error: contentError } = await supabase
        .from('content')
        .select('*')
        .eq('id', contentId)
        .single();

      if (contentError) throw contentError;

      // Fetch associated configurations
      const { data: configData, error: configError } = await supabase
        .from('content_configs')
        .select('*')
        .eq('content_id', contentId);

      if (configError) throw configError;

      setContentWithConfig({
        content: contentData,
        configs: configData || [],
      });
    } catch (error) {
      console.error('Error fetching content with config:', error);
    } finally {
      setLoading(false);
    }
  }

  return {
    contentWithConfig,
    loading,
  };
}
